import { Fragment } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiMenu } from "@miplatform/components";
import { IActionButtonConfig, IMenuItemConfig } from "@miplatform/types";
import { formatDate, isRecentlyAdded } from "@miplatform/utils";
import { Box, Divider, Typography } from "@mui/material";

export function ConfigCard({
  title,
  additionalTitleText,
  additionalCardTextBold,
  additionalCardText,
  modifiedOn,
  createdOn,
  LeftComponent,
  actionButtons,
  menuActions,
  customAction,
  onClick,
  isSelected = false,
}: Readonly<IConfigCardProps>) {
  const { classes, theme } = useStyles({
    clickable: Boolean(onClick),
    isSelected,
  });
  const isNew = isRecentlyAdded(createdOn);

  return (
    <Box className={classes.configListCard} onClick={onClick}>
      <Box className={classes.configListCardContent}>
        {LeftComponent && <Box>{LeftComponent}</Box>}
        <Box className={classes.configListCardText}>
          <Box className={classes.configListCardTextLine}>
            <Typography variant="HD2" color={theme.palette.primary.main}>
              {title}
            </Typography>
            {additionalTitleText?.map((text, index) => (
              <Fragment key={`${text}-${index}`}>
                <Divider orientation="vertical" />
                <Typography variant="BD2" color={theme.palette.text.secondary}>
                  {text}
                </Typography>
              </Fragment>
            ))}
          </Box>
          <Box className={classes.configListCardTextLine}>
            {isNew && (
              <Box className={classes.newBadge}>
                <Typography variant="BD1">New</Typography>
              </Box>
            )}
            {modifiedOn && (
              <>
                <Typography variant="HD1">Last updated: </Typography>
                <Typography variant="HD1" fontWeight={400}>
                  {formatDate(modifiedOn, "MM/DD/YYYY")}
                </Typography>
              </>
            )}
            {additionalCardTextBold && additionalCardText ? (
              <>
                <Typography variant="HD1">{additionalCardTextBold}</Typography>
                <Typography variant="HD1" fontWeight={400}>
                  {additionalCardText}
                </Typography>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Box className={classes.configListCardContent}>
        {actionButtons?.map((button) => (
          <MiButton
            key={button.name}
            title={button.name}
            color={button.color ?? "primary"}
            variant={button.variant ?? "contained"}
            {...button}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (button.onClick) {
                button.onClick();
              }
            }}
          />
        ))}
        <Box>{customAction}</Box>
        {menuActions && menuActions.length > 0 && (
          <Box>
            <MiMenu data={menuActions} />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles<{ clickable: boolean; isSelected: boolean }>()(
  (theme, { clickable, isSelected }) => ({
    configListCard: {
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(2),
      flexGrow: 1,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      border: isSelected
        ? `1px solid ${theme.palette.primary.main}`
        : `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      boxShadow: "0 2px 2px 0 rgba(0, 0, 0, 0.12)",
      "&:hover": clickable
        ? {
            cursor: "pointer",
            backgroundColor: theme.palette.grey.light,
          }
        : undefined,
    },
    configListCardContent: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    configListCardText: {
      display: "flex",
      flexDirection: "column",
    },
    configListCardTextLine: {
      height: 16,
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(1),
    },
    newBadge: {
      backgroundColor: theme.palette.warning.light,
      border: `1px solid ${theme.palette.warning.main}`,
      color: theme.palette.warning.main,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0, 0.5),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  })
);

export interface IConfigCardProps {
  title?: string | React.ReactNode;
  additionalTitleText?: string[];
  additionalCardTextBold?: string;
  additionalCardText?: string;
  modifiedOn?: string;
  createdOn?: string;
  LeftComponent?: React.ReactNode;
  actionButtons?: IActionButtonConfig[];
  menuActions?: IMenuItemConfig[];
  customAction?: React.ReactNode;
  onClick?: () => void;
  isSelected?: boolean;
}
